import React, { useEffect, useRef, useState } from 'react';
import { Camera, RotateCcw, Play, ArrowLeft, Power, Share as ShareIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import { shareImage } from '../utils/shareImage';

const RecapCard = ({ stats, path, onRestart, onContinue }) => {
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const mapRef = useRef(null);

  const handleShare = async (url) => {
    const target = url || generatedImage;
    if (!target || isSharing) return;
    setIsSharing(true);
    try {
      await shareImage(target, 'benderquest-recap.png', 'My BenderQuest Recap');
    } finally {
      setIsSharing(false);
    }
  };

  // --- 0. DETECT PWA MODE (For Safe Area Padding) ---
  useEffect(() => {
    const checkStandalone = () => {
      const isiOSApp = window.navigator && window.navigator.standalone;
      const isMedia = window.matchMedia('(display-mode: standalone)').matches;
      const isNativeApp = window.Capacitor?.isNativePlatform?.();
      return !!(isiOSApp || isMedia || isNativeApp);
    };
    setIsStandalone(checkStandalone());
  }, []);

  // --- 1. DISPLAY MAP (For Capture) ---
  useEffect(() => {
    if (generatedImage) return;

    const timer = setTimeout(() => {
      const container = document.getElementById('recap-map-container');
      if (!container || !window.L) return;

      const L = window.L;
      if (mapRef.current) mapRef.current.remove();
      
      const map = L.map('recap-map-container', { 
        zoomControl: false, dragging: false, scrollWheelZoom: false, 
        attributionControl: false, doubleClickZoom: false, touchZoom: false,
        zoomSnap: 0.1, preferCanvas: true
      });

      if (path && path.length > 0) {
        L.polyline(path, { 
            color: '#ffffff', 
            weight: 4, 
            opacity: 1, 
            dashArray: '10, 10', 
            lineCap: 'round' 
        }).addTo(map);
        
        L.circleMarker(path[0], { radius: 4, color: '#ffffff', fillOpacity: 1 }).addTo(map);
        L.circleMarker(path[path.length - 1], { radius: 4, color: '#ffffff', fillOpacity: 1 }).addTo(map);

        map.fitBounds(L.polyline(path).getBounds(), { padding: [20, 20] });
      } else {
        map.setView([33.4942, -111.9261], 14);
      }
      mapRef.current = map;
    }, 500); 
    return () => clearTimeout(timer);
  }, [path, generatedImage]);

  // --- 2. GENERATE IMAGE LOGIC ---
  const handleGenerate = async () => {
    setIsGenerating(true);
    const element = document.getElementById('recap-capture-area');
    if (!element) return;

    try {
        await new Promise(resolve => setTimeout(resolve, 200));
        
        const canvas = await html2canvas(element, {
            useCORS: true, 
            scale: 3, 
            backgroundColor: null, 
            logging: false,
            onclone: (doc) => {
                const el = doc.getElementById('recap-capture-area');
                if (el) {
                    el.style.background = 'transparent';
                    el.style.marginTop = '0'; 
                }
            }
        });

        const dataUrl = canvas.toDataURL('image/png');
        setGeneratedImage(dataUrl);
        // Auto-open the native share sheet with the freshly rendered recap.
        handleShare(dataUrl);

    } catch (err) { console.error("Gen Error:", err); }
    finally { setIsGenerating(false); }
  };

  return (
    <div className={`fixed inset-0 z-[5000] bg-zinc-950 flex flex-col items-center justify-between px-4 font-sans overflow-y-auto no-scrollbar ${isStandalone ? 'pt-safe pb-safe' : 'py-6'}`}>
        
        {/* --- VIEW 1: PREVIEW & GENERATE --- */}
        {!generatedImage && (
            <div className="w-full flex-1 flex flex-col items-center justify-between animate-in fade-in max-w-md mx-auto">
                
                <div className="text-center shrink-0 w-full pt-2">
                    <h2 className="text-white font-black uppercase text-xl italic tracking-tighter">Session Complete</h2>
                    <p className="text-zinc-500 text-xs font-bold mt-1">Create your recap sticker</p>
                </div>

                {/* ADAPTIVE CAPTURE AREA */}
                <div className="flex-1 flex items-center justify-center w-full min-h-0 py-4">
                    <div 
                        id="recap-capture-area" 
                        className="relative w-full max-w-[320px] flex flex-col items-center gap-4 p-4 bg-transparent shrink-0"
                        style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }} 
                    >
                        <div className="text-center">
                            <h1 className="text-3xl font-black italic text-white tracking-tighter uppercase drop-shadow-md leading-none">
                                BENDERQUEST
                            </h1>
                            <p className="text-[10px] font-bold text-white uppercase tracking-[0.4em] mt-1 drop-shadow-sm">
                                OFFICIAL RECAP
                            </p>
                        </div>

                        <div className="w-full aspect-[2/1] min-h-[100px] relative">
                            <div id="recap-map-container" className="w-full h-full bg-transparent"></div>
                        </div>

                        <div className="flex flex-col items-center gap-3 w-full">
                            <div className="flex flex-col items-center">
                                <span className="text-xs font-bold text-white/70 uppercase tracking-widest mb-0.5">DRINKS</span>
                                <span className="text-3xl font-black text-white italic leading-none">{stats.drinks}</span>
                            </div>
                            <div className="flex flex-col items-center">
                                <span className="text-xs font-bold text-white/70 uppercase tracking-widest mb-0.5">DURATION</span>
                                <span className="text-3xl font-black text-white italic leading-none">
                                    {stats.duration.split(':')[0]}<span className="text-sm">H</span> {stats.duration.split(':')[1]}<span className="text-sm">M</span>
                                </span>
                            </div>
                            <div className="flex flex-col items-center">
                                <span className="text-xs font-bold text-white/70 uppercase tracking-widest mb-0.5">STEPS</span>
                                <span className="text-3xl font-black text-white italic leading-none">{stats.steps}</span>
                            </div>
                        </div>

                        <div className="mt-2">
                            <img
                                src="/bender-logo-white.png"
                                alt="Bender Extender"
                                className="w-24 h-auto object-contain"
                            />
                        </div>
                    </div>
                </div>

                {/* BUTTON GROUP (Anchored at bottom) */}
                <div className="w-full max-w-[320px] flex flex-col gap-3 shrink-0 pb-2">
                    <button 
                        onClick={handleGenerate} 
                        disabled={isGenerating}
                        className="w-full bg-white hover:bg-zinc-200 text-black font-black py-4 rounded-2xl flex items-center justify-center gap-2 uppercase tracking-widest shadow-[0_0_25px_rgba(255,255,255,0.2)] active:scale-95 transition-all"
                    >
                        {isGenerating ? <span className="animate-pulse">Rendering...</span> : <><Camera size={20} /> Create Recap Image</>}
                    </button>

                    <button 
                        onClick={onContinue} 
                        className="w-full bg-zinc-900 border border-zinc-800 text-zinc-400 font-bold py-4 rounded-2xl flex items-center justify-center gap-2 uppercase tracking-widest active:scale-95 transition-all hover:text-white hover:border-zinc-700"
                    >
                        <Play size={18} /> Resume Bender
                    </button>

                    <button 
                        onClick={onRestart} 
                        className="w-full bg-red-900/10 border border-red-900/30 text-red-500 font-bold py-4 rounded-2xl flex items-center justify-center gap-2 uppercase tracking-widest active:scale-95 transition-all hover:bg-red-900/30 hover:text-red-400 mt-2"
                    >
                        <Power size={18} /> End & Return to HQ
                    </button>
                </div>
            </div>
        )}

        {/* --- VIEW 2: FINAL IMAGE & ACTIONS --- */}
        {generatedImage && (
            <div className="w-full flex-1 flex flex-col items-center justify-between animate-in zoom-in-95 duration-300 max-w-md mx-auto pt-4 pb-2">
                
                <div className="text-center w-full shrink-0">
                    <button
                        onClick={() => handleShare()}
                        disabled={isSharing}
                        className="inline-flex items-center gap-2 bg-white text-black font-black text-sm uppercase tracking-widest px-8 py-3 rounded-xl mb-4 shadow-lg active:scale-95 transition-all disabled:opacity-60"
                    >
                        <ShareIcon size={16} /> {isSharing ? 'Sharing…' : 'Share / Save'}
                    </button>
                </div>

                {/* THE FINAL PNG - SCALED DYNAMICALLY */}
                <div className="flex-1 flex items-center justify-center w-full min-h-0 py-4">
                    <img 
                        src={generatedImage} 
                        alt="Bender Recap" 
                        className="w-full max-w-[320px] max-h-full object-contain drop-shadow-2xl relative z-50 pointer-events-auto select-none rounded-xl"
                        style={{ 
                            WebkitTouchCallout: 'default',
                            WebkitUserSelect: 'none',
                            // Checkerboard
                            backgroundImage: 'linear-gradient(45deg, #18181b 25%, transparent 25%), linear-gradient(-45deg, #18181b 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #18181b 75%), linear-gradient(-45deg, transparent 75%, #18181b 75%)',
                            backgroundSize: '20px 20px',
                            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                        }}
                    />
                </div>

                {/* FINAL BUTTONS */}
                <div className="w-full max-w-[320px] flex flex-col gap-3 shrink-0">
                    <button 
                        onClick={() => setGeneratedImage(null)}
                        className="w-full bg-zinc-800 text-white font-bold py-4 rounded-2xl uppercase tracking-widest shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2 hover:bg-zinc-700"
                    >
                        <ArrowLeft size={18} /> Edit / Redo
                    </button>

                    <div className="flex gap-2 w-full">
                        <button 
                            onClick={onContinue}
                            className="flex-1 bg-zinc-900 border border-zinc-800 text-zinc-400 font-bold py-3.5 rounded-xl uppercase tracking-widest shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2 text-xs hover:text-white"
                        >
                            <Play size={16} /> Resume
                        </button>

                        <button 
                            onClick={onRestart} 
                            className="flex-1 bg-red-900/20 border border-red-900/50 text-red-400 font-bold py-3.5 rounded-xl uppercase tracking-widest shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2 text-xs hover:text-red-300"
                        >
                            <Power size={16} /> Return to HQ
                        </button>
                    </div>
                </div>
            </div>
        )}

    </div>
  );
};

export default RecapCard;
